import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TrainingLog({ active }) {
  const [rows, setRows] = useState(null);
  const [member, setMember] = useState("");
  const [topic, setTopic] = useState("");
  const [dateCompleted, setDateCompleted] = useState("");
  const [trainer, setTrainer] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const fetchRows = useCallback(() => {
    axios.get(`${API}/trainings`).then((r) => setRows(r.data)).catch(() => setRows([]));
  }, []);
  useEffect(() => { if (active === "trn") fetchRows(); }, [active, fetchRows]);

  const canSubmit = member.trim() && topic.trim() && dateCompleted && !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/trainings`, { member, topic, date_completed: dateCompleted, trainer, notes });
      toast.success(`Training recorded — ${member}: ${topic}`);
      setMember(""); setTopic(""); setDateCompleted(""); setTrainer(""); setNotes("");
      fetchRows();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not record training");
    }
    setSubmitting(false);
  };

  return (
    <section className={`chapter utility${active === "trn" ? " active" : ""}`} id="trn">
      <div className="eyebrow">Working record · executes ECH-SOP-2.4 — training that isn't evidenced didn't happen</div>
      <h1>Training Log</h1>
      <p className="lede">Every completed training — onboarding compliance, annual HIPAA refresh, OSHA, role-specific modules — recorded with the member, topic, date, and trainer. This log reconciles against the roster at MOCR question C-4.</p>

      <div className="formcard" data-testid="trn-form">
        <div className="formcard-title">Record a completed training</div>
        <div className="fld-grid three">
          <div className="fld"><label>Team member</label><input className="inp" value={member} onChange={(e) => setMember(e.target.value)} placeholder="Name" data-testid="trn-member" /></div>
          <div className="fld"><label>Training topic</label><input className="inp" value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="e.g. Annual HIPAA refresh" data-testid="trn-topic" /></div>
          <div className="fld"><label>Date completed</label><input className="inp" type="date" value={dateCompleted} onChange={(e) => setDateCompleted(e.target.value)} data-testid="trn-date" /></div>
        </div>
        <div className="fld-grid">
          <div className="fld"><label>Trainer / source</label><input className="inp" value={trainer} onChange={(e) => setTrainer(e.target.value)} placeholder="Who delivered it (person or vendor)" data-testid="trn-trainer" /></div>
          <div className="fld"><label>Notes</label><input className="inp" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Score, certificate ref, follow-up…" data-testid="trn-notes" /></div>
        </div>
        <button className="btn" onClick={submit} disabled={!canSubmit} data-testid="trn-submit">Record training</button>
      </div>

      <h2>Log {rows ? `— ${rows.length} record${rows.length === 1 ? "" : "s"}` : ""}</h2>
      <table data-testid="trn-table">
        <tbody>
          <tr><th>Member</th><th>Topic</th><th>Completed</th><th>Trainer</th><th>Notes</th><th>Recorded by (account)</th></tr>
          {rows === null ? (
            <tr><td colSpan={6}>Loading…</td></tr>
          ) : rows.length === 0 ? (
            <tr><td colSpan={6}>No trainings recorded yet.</td></tr>
          ) : (
            rows.map((r) => (
              <tr key={r.id}>
                <td>{r.member}</td>
                <td>{r.topic}</td>
                <td>{r.date_completed}</td>
                <td>{r.trainer || "—"}</td>
                <td>{r.notes || "—"}</td>
                <td>{r.recorded_by ? `${r.recorded_by.name}` : "—"}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      <p style={{ color: "var(--mute)", fontSize: "13px" }}>Retention: 7 years with workforce records. Annual HIPAA and OSHA refreshers are mandatory for every lane (SOP 2.4 / 6.5 / 9.3).</p>
    </section>
  );
}
